import React, { useState } from "react";
import {
  Braces,
  Eye,
  LucideWrench,
  Plus,
  Settings,
  Settings2,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import { useEmailStore } from "../../store/emailStore";
import { useEffect } from "react";

const EmailVariablesBuilder = ({ setCustomVariables }) => {
  const { showInstruction, setShowInstruction } = useEmailStore();
  const [variables, setVariables] = useState([{ key: "", value: "" }]);
  const [generatedVariables, setGeneratedVariables] = useState(null);

  const [errors, setErrors] = useState({});

  const handleChange = (index, field, value) => {
    const updated = [...variables];
    updated[index][field] = value;
    setVariables(updated);
  };

  const addVariable = () => {
    setVariables([...variables, { key: "", value: "" }]);
  };

  const removeVariable = (index) => {
    const updated = variables.filter((_, i) => i !== index);
    setVariables(updated);
  };

  // 🔥 Validation Logic
  const validate = () => {
    let newErrors = {};
    let keys = [];

    variables.forEach((item, index) => {
      if (!item.key.trim()) {
        newErrors[`key-${index}`] = "Key is required";
      }

      if (!item.value.trim()) {
        newErrors[`value-${index}`] = "Value is required";
      }

      if (item.key.trim()) {
        if (keys.includes(item.key.trim())) {
          newErrors[`key-${index}`] = "Duplicate key";
        } else {
          keys.push(item.key.trim());
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateVariablesObject = () => {
    const result = {};
    variables.forEach((item) => {
      result[item.key.trim()] = item.value.trim();
    });
    return result;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const finalVariables = generateVariablesObject();
    setCustomVariables(finalVariables);
    setGeneratedVariables(finalVariables);

    console.log("Final Variables:", finalVariables);
  };

  return (
    <div className="w-full mb-4 mt-4 max-w-xl mx-auto border-t-8 border-t-blue-700 bg-white shadow-lg rounded-2xl p-6 space-y-6">
      {generatedVariables && (
        <div className="mt-4 rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-white shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-green-100 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100 text-green-600">
              <Braces size={20} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-green-900">
                Your Custom Variables
              </h3>
              <p className="text-xs text-green-600">
                These variables will be available inside your template.
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-60 overflow-auto px-5 py-4">
            <pre className="rounded-xl bg-white p-4 text-xs text-gray-800 shadow-inner">
              {JSON.stringify(generatedVariables, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <div className="flex w-full justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          Custom Variables
        </h2>

        <button
          type="button"
          onClick={() => setShowInstruction()}
          className={` text-sm  ${showInstruction ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"} p-2 rounded-lg hover:bg-green-50 hover:text-green-700 flex items-center gap-2 transition`}
        >
          {showInstruction ? (
            <>
              <X />
              Close Instructions
            </>
          ) : (
            <>
              <Eye />
              View Instructions
            </>
          )}
        </button>
      </div>

      <div className="space-y-4">
        {variables.map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="flex gap-3 items-center">
              <input
                type="text"
                placeholder="Key"
                value={item.key}
                onChange={(e) => handleChange(index, "key", e.target.value)}
                className={`w-1/2 px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${
                  errors[`key-${index}`]
                    ? "border-red-400 focus:ring-red-300"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
              />

              <input
                type="text"
                placeholder="Value"
                value={item.value}
                onChange={(e) => handleChange(index, "value", e.target.value)}
                className={`w-1/2 px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${
                  errors[`value-${index}`]
                    ? "border-red-400 focus:ring-red-300"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
              />

              {variables.length > 1 && (
                <button
                  onClick={() => removeVariable(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>

            {/* Error Messages */}
            <div className="flex gap-4 text-sm text-red-500">
              {errors[`key-${index}`] && <span>{errors[`key-${index}`]}</span>}
              {errors[`value-${index}`] && (
                <span>{errors[`value-${index}`]}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Button */}
      <button
        onClick={addVariable}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
      >
        <Plus size={18} />
        Add Variable
      </button>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        className="w-full mt-4 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
      >
        Generate Variables JSON
      </button>
    </div>
  );
};

export default EmailVariablesBuilder;
